/**
 * KISS dashboard workspace hub — three pillar cards using existing routes only.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Flame, Lock, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const CARD_CLASS =
  'flex h-full flex-col rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-gradient-to-br from-[hsl(42_30%_100%)] via-[hsl(42_28%_99%)] to-[hsl(40_22%_97%)] p-5 shadow-[0_8px_32px_-20px_hsl(28_14%_18%_/_0.12)] sm:p-6';

function WorkspaceCard({ id, icon: Icon, title, copy, ctaLabel, ctaTo, testId }) {
  return (
    <article className={CARD_CLASS} data-testid={`workspace-pillar-${id}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(38_22%_88%)]/50 text-[#5c4a32]">
        <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
      </span>
      <h3 className="mt-4 font-serif text-xl font-light tracking-tight text-[#1c1917]">{title}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-[#5c534a]">{copy}</p>
      <Button
        asChild
        className="mt-5 w-full rounded-full bg-[#8B7355] text-white hover:bg-[#7A6548] sm:w-auto"
        data-testid={testId}
      >
        <Link to={ctaTo}>
          {ctaLabel}
          <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
        </Link>
      </Button>
    </article>
  );
}

const WORKSPACES = [
  {
    id: 'story-studio',
    icon: Sparkles,
    title: 'AI Story Studio',
    copy: 'Capture stories, voice memories and life chapters.',
    ctaLabel: 'Open Story Studio',
    ctaTo: '/story-studio',
    testId: 'workspace-open-story-studio',
  },
  {
    id: 'handover-vault',
    icon: Lock,
    title: 'Family Handover Vault',
    copy: 'Keep practical guidance, trusted contacts and important wishes in one private place.',
    ctaLabel: 'Open Vault',
    ctaTo: '/vault',
    testId: 'workspace-open-vault',
  },
  {
    id: 'memorial-bridge',
    icon: Flame,
    title: 'Memorial Bridge',
    copy: 'Prepare memorial pages, tributes, QR-linked keepsakes and remembrance spaces.',
    ctaLabel: 'Open Memorial Bridge',
    ctaTo: '/memorials',
    testId: 'workspace-open-memorial-bridge',
  },
];

export function ChooseWorkspaceSection() {
  return (
    <section className="space-y-5" data-testid="choose-workspace-section" aria-labelledby="choose-workspace-heading">
      <div className="space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">Your workspaces</p>
        <h2 id="choose-workspace-heading" className="font-serif text-2xl font-light tracking-tight text-[#1c1917] sm:text-[1.65rem]">
          Choose your workspace
        </h2>
        <p className="max-w-2xl text-sm leading-relaxed text-[#5c534a]">
          Pick one area to focus on. Your archive, people and family tree are the foundation — not a separate pillar.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
        {WORKSPACES.map((workspace) => (
          <WorkspaceCard key={workspace.id} {...workspace} />
        ))}
      </div>
    </section>
  );
}

export default ChooseWorkspaceSection;
